import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Auth0Provider } from "@auth0/auth0-react";
import posthog from 'posthog-js';

// Load CookieYes banner if configured
const cookieYesId = process.env.REACT_APP_COOKIEYES_ID;
const cookieYesEnabled = !!cookieYesId;
if (cookieYesEnabled) {
  const script = document.createElement('script');
  script.id = 'cookieyes';
  script.type = 'text/javascript';
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://cdn-cookieyes.com/client_data/${cookieYesId}/script.js`;
  script.onerror = (e) => {
    // Surface third-party loading errors instead of generic "Script error."
    // eslint-disable-next-line no-console
    console.error('CookieYes script failed to load', e);
  };
  document.head.appendChild(script);
}

// Helpers to read CookieYes consent (cookieyes-consent uses comma-separated key:value pairs)
const parseCookieYes = (): Record<string, string> => {
  try {
    const entry = (document.cookie || '').split(';').find(c => c.trim().startsWith('cookieyes-consent='));
    if (!entry) return {} as Record<string, string>;
    const val = decodeURIComponent(entry.split('=')[1] || '');
    const obj = val.split(',').reduce((acc, item) => {
      const [k, v] = item.split(':');
      if (k && v) (acc as any)[k] = v;
      return acc;
    }, {} as Record<string, string>);
    return obj;
  } catch {
    return {} as Record<string, string>;
  }
};

const hasAnalyticsConsent = () => {
  // Always grant consent on localhost or when CookieYes is disabled
  if (!cookieYesEnabled || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return true;
  const c = parseCookieYes();
  return !!(c.analytics === 'yes' || c.performance === 'yes' || c.statistics === 'yes');
};

const hasMarketingConsent = () => {
  // Always grant consent on localhost or when CookieYes is disabled
  if (!cookieYesEnabled || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return true;
  const c = parseCookieYes();
  return !!(c.marketing === 'yes' || c.advertisement === 'yes');
};

// Inject Google Tag Manager AFTER Analytics consent (and propagate Consent Mode)
const tagManagerId = process.env.REACT_APP_TAG_MANAGER_ID;
const loadGTMWithConsent = () => {
  if (!tagManagerId) return;
  // Avoid multiple loads
  if (document.getElementById('gtm-script')) return;

  // Default consent denied
  (window as any).dataLayer = (window as any).dataLayer || [];
  const gtag = function() { (window as any).dataLayer.push(arguments as any); } as any;
  gtag('consent', 'default', {
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    ad_storage: 'denied',
    analytics_storage: 'denied',
  });

  // Grant based on current CookieYes state
  if (hasAnalyticsConsent()) {
    gtag('consent', 'update', { analytics_storage: 'granted' });
  }
  if (hasMarketingConsent()) {
    gtag('consent', 'update', {
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      ad_storage: 'granted',
    });
  }

  // Load GTM
  (window as any).dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const f = document.getElementsByTagName('script')[0] as HTMLElement;
  const j = document.createElement('script');
  j.id = 'gtm-script';
  j.async = true;
  (j as any).crossOrigin = 'anonymous';
  j.src = `https://www.googletagmanager.com/gtm.js?id=${tagManagerId}`;
  j.onerror = (e) => {
    // eslint-disable-next-line no-console
    console.error('GTM script failed to load', e);
  };
  f.parentNode?.insertBefore(j, f);
};

// If analytics consent is present now, load GTM; otherwise, wait for CookieYes event
if (hasAnalyticsConsent()) {
  loadGTMWithConsent();
}
if (cookieYesEnabled) window.addEventListener('cookieyes_consent_update', () => {
  // Update consent in GTM (if loaded) and load if analytics now granted
  if (hasAnalyticsConsent()) {
    loadGTMWithConsent();
  }
  // Update Consent Mode after change
  const gtag = function() { (window as any).dataLayer?.push(arguments as any); } as any;
  gtag('consent', 'update', {
    analytics_storage: hasAnalyticsConsent() ? 'granted' : 'denied',
    ad_user_data: hasMarketingConsent() ? 'granted' : 'denied',
    ad_personalization: hasMarketingConsent() ? 'granted' : 'denied',
    ad_storage: hasMarketingConsent() ? 'granted' : 'denied',
  });
});

// Initialize PostHog if configured (respect CookieYes consent; bypass when CookieYes disabled)
// CRA exposes only REACT_APP_* at build time
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST;
if (POSTHOG_KEY) {
  const initPosthog = () => {
    if ((posthog as any)._initialized) return;
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: true,
      autocapture: true,
      debug: false,
      session_recording: {
        enabled: true,
        recordCrossOriginIframes: false,
      },
    } as any);
    (posthog as any)._initialized = true;
    try { posthog.capture('$pageview'); } catch {}
  };

  if (!cookieYesEnabled) {
    initPosthog();
  } else {
    const hasAnalyticsConsent = () => {
      // Always grant consent on localhost
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return true;
      try {
        const entry = (document.cookie || '').split(';').find(c => c.trim().startsWith('cookieyes-consent='));
        if (!entry) return false;
        const val = decodeURIComponent(entry.split('=')[1] || '');
        const obj = val.split(',').reduce((acc, item) => {
          const [k, v] = item.split(':');
          if (k && v) (acc as any)[k] = v;
          return acc;
        }, {} as Record<string, string>);
        const hasConsent = !!(obj.analytics === 'yes' || obj.performance === 'yes' || obj.statistics === 'yes');
        return hasConsent;
      } catch { return false; }
    };

    if (hasAnalyticsConsent()) initPosthog();
    window.addEventListener('cookieyes_consent_update', () => {
      if (hasAnalyticsConsent()) {
        initPosthog();
        try { 
          (posthog as any).opt_in_capturing && (posthog as any).opt_in_capturing();
          // Ensure session recording is enabled after opt-in
          if ((posthog as any).startSessionRecording) {
            (posthog as any).startSessionRecording();
          }
        } catch {}
      } else {
        try {
          (posthog as any).opt_out_capturing && (posthog as any).opt_out_capturing();
          (posthog as any).reset && (posthog as any).reset();
        } catch {}
      }
    });
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const onRedirectCallback = (appState: any) => {
  // Handle the redirect callback
  // Clean up the URL by removing query parameters
  // This will be handled by React Router's Navigate component
  if (window.location.pathname === '/' && window.location.search) {
    // Replace the current URL without the query parameters
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }
};

root.render(
  <React.StrictMode>
    {/* Google Tag Manager (noscript) */}
    {!tagManagerId ? null : (
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${tagManagerId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    )}
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN || ''}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID || ''}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email read:organizations roles offline_access'
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      onRedirectCallback={onRedirectCallback}
    >
      <App />
    </Auth0Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
